import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import AnalyzingOverlay from "../../components/UI/AnalyzingOverlay";
import { analyzeImage } from "../../services/api";
import {
  validateImageFile,
  validateImageContent,
} from "../../utils/imageValidation";
import toothImage from "../../assets/images/tooth.png";
import styles from "./HomePage.module.css";

const BrainIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="#000000">
    <g>
      <path
        d="M11.26,6.86l-3.39-2a.75.75,0,1,1,.78-1.28l3.36,2Z"
        style={{ fill: "#aecbfa", fillRule: "evenodd" }}
      />
      <path
        d="M11.52,10.31,4.69,6.36a.75.75,0,0,1,.75-1.3l6.08,3.52Z"
        style={{ fill: "#aecbfa", fillRule: "evenodd" }}
      />
      <path
        d="M19.6,5.33a.76.76,0,0,0-1-.27L12.75,8.4V6.85l3.39-2a.74.74,0,0,0,.25-1,.75.75,0,0,0-1-.25L11.65,5.78a.79.79,0,0,0-.39.68V21a.75.75,0,1,0,1.49,0V10.14l6.57-3.78A.76.76,0,0,0,19.6,5.33Z"
        style={{ fill: "#669df6", fillRule: "evenodd" }}
      />
      <circle cx="12.01" cy="3.09" r="0.84" style={{ fill: "#669df6" }} />
      <path
        d="M9.48,10.82a.73.73,0,0,1,.38.64v4.08a.74.74,0,0,1-.38.65.79.79,0,0,1-.37.1.86.86,0,0,1-.38-.1L4.41,13.7a.75.75,0,1,1,.75-1.3l3.2,1.84V11.9l-1.45-.83a.75.75,0,0,1-.27-1,.74.74,0,0,1,1-.27Z"
        style={{ fill: "#aecbfa" }}
      />
      <circle cx="5.26" cy="9.26" r="0.84" style={{ fill: "#aecbfa" }} />
      <path
        d="M16.34,9.77a.74.74,0,0,1,1,.27.75.75,0,0,1-.27,1l-1.45.83v2.34l3.2-1.84a.75.75,0,1,1,.75,1.3l-4.32,2.49a.86.86,0,0,1-.38.1.79.79,0,0,1-.37-.1.74.74,0,0,1-.38-.65V11.46a.73.73,0,0,1,.38-.64Z"
        style={{ fill: "#4285f4" }}
      />
      <circle cx="18.74" cy="9.26" r="0.84" style={{ fill: "#4285f4" }} />
    </g>
  </svg>
);

const BoltIcon = () => (
  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M38 4L20 36H32L26 60L44 28H32L38 4Z"
      fill="url(#boltGradient)"
      stroke="#F59E0B"
      strokeWidth="2"
    />
    <defs>
      <linearGradient
        id="boltGradient"
        x1="20"
        y1="4"
        x2="44"
        y2="60"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0%" stopColor="#FCD34D" />
        <stop offset="50%" stopColor="#F59E0B" />
        <stop offset="100%" stopColor="#DC2626" />
      </linearGradient>
    </defs>
  </svg>
);

const ExportIcon = () => (
  <svg
    version="1.1"
    viewBox="0 0 303.188 303.188"
    xmlns="http://www.w3.org/2000/svg"
    xmlnsXlink="http://www.w3.org/1999/xlink"
    xmlSpace="preserve"
  >
    <g>
      <polygon
        points="219.821,0 32.842,0 32.842,303.188 270.346,303.188 270.346,50.525"
        style={{ fill: "#E4E4E4" }}
      />
      <polygon
        points="227.64,25.263 32.842,25.263 32.842,0 219.821,0"
        style={{ fill: "#007934" }}
      />
      <g>
        <g>
          <path
            d="M114.872,227.984c-2.982,0-5.311,1.223-6.982,3.666c-1.671,2.444-2.507,5.814-2.507,10.109 c0,8.929,3.396,13.393,10.188,13.393c2.052,0,4.041-0.285,5.967-0.856c1.925-0.571,3.86-1.259,5.808-2.063v10.601 c-3.872,1.713-8.252,2.57-13.14,2.57c-7.004,0-12.373-2.031-16.107-6.094c-3.734-4.062-5.602-9.934-5.602-17.615 c0-4.803,0.904-9.023,2.714-12.663c1.809-3.64,4.411-6.438,7.808-8.395c3.396-1.957,7.39-2.937,11.98-2.937 c5.016,0,9.808,1.09,14.378,3.27l-3.841,9.871c-1.713-0.805-3.428-1.481-5.141-2.031 C118.681,228.26,116.841,227.984,114.872,227.984z"
            style={{ fill: "#A4A9AD" }}
          />
          <path
            d="M166.732,250.678c0,2.878-0.729,5.433-2.191,7.665c-1.459,2.232-3.565,3.967-6.315,5.205 c-2.751,1.237-5.977,1.856-9.681,1.856c-3.089,0-5.681-0.217-7.775-0.65c-2.095-0.434-4.274-1.191-6.538-2.27v-11.172 c2.391,1.227,4.877,2.186,7.458,2.872c2.582,0.689,4.951,1.032,7.109,1.032c1.862,0,3.227-0.322,4.095-0.969 c0.867-0.645,1.302-1.476,1.302-2.491c0-0.635-0.175-1.19-0.524-1.666c-0.349-0.477-0.91-0.958-1.682-1.444 c-0.772-0.486-2.83-1.48-6.173-2.983c-3.026-1.375-5.296-2.708-6.809-3.999s-2.634-2.771-3.364-4.443s-1.095-3.65-1.095-5.936 c0-4.273,1.555-7.605,4.666-9.997c3.109-2.391,7.384-3.587,12.822-3.587c4.803,0,9.7,1.111,14.694,3.333l-3.841,9.681 c-4.337-1.989-8.082-2.984-11.234-2.984c-1.63,0-2.814,0.286-3.555,0.857s-1.111,1.28-1.111,2.127 c0,0.91,0.471,1.725,1.412,2.443c0.941,0.72,3.496,2.031,7.665,3.936c3.999,1.799,6.776,3.729,8.331,5.792 C165.955,244.949,166.732,247.547,166.732,250.678z"
            style={{ fill: "#A4A9AD" }}
          />
          <path
            d="M199.964,218.368h14.027l-15.202,46.401H184.03l-15.139-46.401h14.092l6.316,23.519 c1.312,5.227,2.031,8.865,2.158,10.918c0.148-1.481,0.443-3.333,0.889-5.555c0.443-2.222,0.835-3.967,1.174-5.236 L199.964,218.368z"
            style={{ fill: "#A4A9AD" }}
          />
        </g>
      </g>
      <polygon
        points="219.821,50.525 270.346,50.525 219.821,0"
        style={{ fill: "#D1D3D3" }}
      />
      <g>
        <rect
          x="134.957"
          y="80.344"
          style={{ fill: "#007934" }}
          width="33.274"
          height="15.418"
        />
        <rect
          x="175.602"
          y="80.344"
          style={{ fill: "#007934" }}
          width="33.273"
          height="15.418"
        />
        <rect
          x="134.957"
          y="102.661"
          style={{ fill: "#007934" }}
          width="33.274"
          height="15.419"
        />
        <rect
          x="175.602"
          y="102.661"
          style={{ fill: "#007934" }}
          width="33.273"
          height="15.419"
        />
        <rect
          x="134.957"
          y="124.979"
          style={{ fill: "#007934" }}
          width="33.274"
          height="15.418"
        />
        <rect
          x="175.602"
          y="124.979"
          style={{ fill: "#007934" }}
          width="33.273"
          height="15.418"
        />
        <rect
          x="94.312"
          y="124.979"
          style={{ fill: "#007934" }}
          width="33.273"
          height="15.418"
        />
        <rect
          x="134.957"
          y="147.298"
          style={{ fill: "#007934" }}
          width="33.274"
          height="15.418"
        />
        <rect
          x="175.602"
          y="147.298"
          style={{ fill: "#007934" }}
          width="33.273"
          height="15.418"
        />
        <rect
          x="94.312"
          y="147.298"
          style={{ fill: "#007934" }}
          width="33.273"
          height="15.418"
        />
        <g>
          <path
            d="M127.088,116.162h-10.04l-6.262-10.041l-6.196,10.041h-9.821l10.656-16.435L95.406,84.04h9.624 l5.8,9.932l5.581-9.932h9.909l-10.173,16.369L127.088,116.162z"
            style={{ fill: "#007934" }}
          />
        </g>
      </g>
    </g>
  </svg>
);

const ShieldIcon = () => (
  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M32 8L16 14V28C16 40 22 50 32 56C42 50 48 40 48 28V14L32 8Z"
      fill="url(#shieldGradient)"
    />
    <path d="M28 36L24 32L22 34L28 40L42 26L40 24L28 36Z" fill="white" />
    <defs>
      <linearGradient
        id="shieldGradient"
        x1="16"
        y1="8"
        x2="48"
        y2="56"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0%" stopColor="#3B82F6" />
        <stop offset="100%" stopColor="#1E40AF" />
      </linearGradient>
    </defs>
  </svg>
);

const points = [
  {
    icon: BrainIcon,
    text: "AI pathology mapping",
    shadowClass: "aiShadow",
  },
  {
    icon: BoltIcon,
    text: "Instant overlays",
    shadowClass: "boltShadow",
  },
  {
    icon: ExportIcon,
    text: "CSV reports",
    shadowClass: "exportShadow",
  },
  {
    icon: ShieldIcon,
    text: "Secure processing",
    shadowClass: "shieldShadow",
  },
];

function HomePage() {
  const fileInputRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [fileDataUrl, setFileDataUrl] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const navigate = useNavigate();
  const { setPage } = useTheme();

  useEffect(() => {
    setPage("home");
    return () => setPage(null);
  }, [setPage]);

  const handleFile = async (file) => {
    if (!file) return;

    setError("");

    const validation = await validateImageFile(file);
    if (!validation.valid) {
      setError(validation.error);
      setFileName("");
      setFileDataUrl("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    const contentValidation = await validateImageContent(file);
    if (!contentValidation.valid && contentValidation.warning) {
      const proceed = window.confirm(
        `${contentValidation.warning}\n\nDo you want to continue anyway?`
      );
      if (!proceed) {
        setFileName("");
        setFileDataUrl("");
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }
    }

    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => setFileDataUrl(String(ev.target?.result || ""));
    reader.readAsDataURL(file);
  };

  const onFileSelect = (e) => {
    const file = e.target.files?.[0];
    handleFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFile(file);
      if (fileInputRef.current) {
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);
        fileInputRef.current.files = dataTransfer.files;
      }
    }
  };

  const startAnalysis = async () => {
    if (!fileDataUrl) {
      setError("Choose an image first.");
      return;
    }
    setError("");
    setIsAnalyzing(true);

    try {
      const file = fileInputRef.current?.files?.[0];
      if (file) {
        try {
          const data = await analyzeImage(file);
          navigate("/analysis", {
            state: {
              image: fileDataUrl,
              name: fileName,
              predictions: data.predictions || null,
            },
          });
        } catch (apiError) {
          console.error("Analysis error:", apiError);
          navigate("/analysis", {
            state: {
              image: fileDataUrl,
              name: fileName,
            },
          });
        }
      } else {
        navigate("/analysis", {
          state: {
            image: fileDataUrl,
            name: fileName,
          },
        });
      }
    } catch (error) {
      console.error("Analysis error:", error);
      navigate("/analysis", {
        state: {
          image: fileDataUrl,
          name: fileName,
        },
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <main className={styles.page}>
      <section className={styles.hero}>
        <div>
          <h1 className={styles.title}>AlphaDent</h1>
          <p className={styles.subtitle}>Advanced Dental Analysis Platform</p>
          <p className={styles.lead}>
            Upload intraoral radiographs or photos to generate precise AI-driven
            segmentations across nine dental pathology classes, presented in a
            clear clinical layout for fast, confident review.
          </p>
        </div>
        <div className={styles.toothWrap}>
          <img
            src={toothImage}
            alt="Tooth illustration"
            className={styles.tooth}
          />
        </div>
      </section>

      <section className={styles.points}>
        {points.map((item, index) => {
          const IconComponent = item.icon;
          return (
            <div key={index} className={styles.point}>
              <div
                className={`${styles.pointIcon} ${styles[item.shadowClass]}`}
              >
                <IconComponent />
              </div>
              <p className={styles.pointText}>{item.text}</p>
            </div>
          );
        })}
      </section>

      <section className={styles.upload}>
        <div className={styles.uploadContent}>
          <div
            className={`${styles.dropZone} ${
              isDragging ? styles.dragging : ""
            } ${fileDataUrl ? styles.dropZoneHasFile : ""}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              className={styles.fileInput}
              onChange={onFileSelect}
            />
            <div className={styles.dropZoneContent}>
              <p className={styles.dropZoneText}>
                {fileName || "Drag & drop an image here or click to browse"}
              </p>
              <button
                className={styles.browseButton}
                onClick={() => fileInputRef.current?.click()}
                disabled={isAnalyzing}
              >
                Browse files
              </button>
            </div>
          </div>
          <div className={styles.uploadActions}>
            <button
              className={styles.primary}
              onClick={startAnalysis}
              disabled={isAnalyzing || !fileDataUrl}
            >
              {isAnalyzing ? "Analyzing..." : "Analyze"}
            </button>
          </div>
          {error && <div className={styles.error}>{error}</div>}
        </div>
      </section>
      {isAnalyzing && <AnalyzingOverlay label="Analyzing..." />}
    </main>
  );
}

export default HomePage;
